import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSubmitFirstStepMutation } from '../../data/mutations/registration';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { useCustomNavigate } from '../../hooks/useCustomNavigate';
import {
	selectRegistrationCheckoxes,
	selectRegistrationEmail,
	selectRegistrationInfo,
	selectRegistrationIsChecked,
	selectRegistrationState,
	setRegistrationEmail,
	setRegistrationIsChecked,
} from '../../services/registration';
import { ROUTES } from '../../types/routes';
import { TCheckbox, TMappedCheckboxes } from '../../types/types';
import { mapClassnameForMobile } from '../../utils';
import { CustomButton } from '../common/buttons/index';
import { APIError } from '../common/error';
import { HeaderLayout } from '../common/header';
import { CustomInput } from '../common/inputs';
import RegistrationCheckboxes from './personal';
import { RegistrationClearModal } from '../common/modals';

const RegistrationPersonalLayout = () => {
	const state = useAppSelector(selectRegistrationState);
	const checkBoxes = useAppSelector(selectRegistrationCheckoxes);
	const isChecked = useAppSelector(selectRegistrationIsChecked);
	const email = useAppSelector(selectRegistrationEmail);
	const stateInfo = useAppSelector(selectRegistrationInfo);

	const { t } = useTranslation();
	const dispatch = useAppDispatch();
	const navigate = useCustomNavigate();
	const buttonRef = useRef(null);

	const { submitFirstStep, isPending, isError, error } = useSubmitFirstStepMutation(
		navigate,
		stateInfo?.isEmailVerified as boolean
	);

	const [isRegistrationClearModalOpen, setIsRegistrationClearModalOpen] =
		useState<boolean>(false);

	const [fakeError, setFakeError] = useState(false);

	useEffect(() => {
		if (isError) {
			setFakeError(isError);
		}
	}, [isError]);

	const handleBackClick = async () => {
		setIsRegistrationClearModalOpen(!isRegistrationClearModalOpen);
	};

	const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		dispatch(setRegistrationEmail(e.target.value));
		setFakeError(false);
	};

	const handleIsChecked = () => {
		if (checkBoxes) {
			return checkBoxes.some(
				(checkbox, idx) =>
					checkbox.isRequired && checkbox.isRequired != isChecked[idx]
			);
		}
	};

	const handleEmailClear = () => {
		dispatch(setRegistrationEmail(''));
	};

	const handleSubmit = async () => {
		setFakeError(false);
		if (
			checkBoxes === undefined ||
			handleIsChecked() ||
			email.length === 0 ||
			!new RegExp(
				/^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/iu
			).test(email)
		) {
			setFakeError(true);
		} else {
			setFakeError(false);
			if (stateInfo?.isEmailVerified) {
				navigate(ROUTES.REGISTRATION.PHONE);
				return;
			} else {
				dispatch(
					setRegistrationIsChecked(
						[false, false, false].map((value, idx) => {
							if (idx < isChecked?.length) {
								return isChecked[idx];
							}
							return false;
						})
					)
				);
				await submitFirstStep({
					state: state,
					email: email,
					checkBoxesResult: checkBoxes
						?.filter((checkbox: TCheckbox) => checkbox.checkBoxId != 'cookie')
						.map(
							//eslint-disable-next-line
							(checkbox: any, idx: number) => {
								return {
									checkBoxId: checkbox.checkBoxId,
									isSelected: isChecked[idx],
								};
							}
						) as TMappedCheckboxes,
					dispatch: dispatch,
				});
			}
		}
	};

	return (
		<div className={'component'}>
			<div className={'innerComponent'}>
				<HeaderLayout isFunctionalHeader handleBackClick={handleBackClick} />
				<RegistrationClearModal
					isModalOpen={isRegistrationClearModalOpen}
					handleIsModalOpen={handleBackClick}
				/>
				<div className={mapClassnameForMobile('walletLayout')}>
					<div className="innerContainer">
						<h1 className={'innerContainerMainText'}>{t('start_signUp')}</h1>
						<CustomInput
							isNonCloseIcon={stateInfo?.isEmailVerified as boolean}
							isError={fakeError}
							isDisabled={stateInfo?.isEmailVerified as boolean}
							isRequired={email?.length > 0}
							type={'email'}
							id={'email'}
							label={t('passwordRecovery_provideYourEmail')}
							value={email}
							onChange={handleEmailChange}
							className={'input'}
							onClearClick={handleEmailClear}
							inputPropsRef={buttonRef}
						/>
						<APIError error={error} isError={isError} />
					</div>
				</div>
			</div>
			<div className="depositButtonsBottomContainer">
				<RegistrationCheckboxes
					setFakeError={setFakeError}
					isError={fakeError}
					checked={isChecked}
				/>
				<CustomButton
					buttonRef={buttonRef}
					text={
						t(
							stateInfo?.isEmailVerified
								? 'registration_continueRegistration'
								: 'login_continue'
						) as string
					}
					type={'submit'}
					onClick={handleSubmit}
					className={'confirmFullWidthButton'}
					isLoading={isPending || checkBoxes === undefined}
				/>
			</div>
		</div>
	);
};

export default RegistrationPersonalLayout;
